import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SystemService } from 'src/app/service/system.service';
import { MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { AdminService } from 'src/app/service/admin.service';
import { EventsService } from 'src/app/service/events.service';
import { NotificationService } from 'src/app/service/notification.service';
import { Event } from 'src/app/model/event';
import * as moment from 'moment';
import { EventReviewConfiguration, ViewersValues, ViewersWithoutOwnValues, AnonymityValues } from 'src/app/model/eventReviewConfiguration';
import { ReviewsConfigurationService } from '../../../../service/reviews.configuration.service';
import { ConfirmationDialogComponent } from 'src/app/component/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { toDayjs } from 'src/app/model/baseModel';
import { forkJoin } from 'rxjs';
import { TimezoneService } from 'src/app/service/timezone.service';
import { getLocale } from 'src/app/util/locale';
import { DatetimepickerInputComponent } from 'src/app/component/datetimepicker-input/datetimepicker-input.component';
import { HasUnsavedChanges } from 'src/app/guard/unsaved-changes.guard';

const MOCK_DOCOLOC_PASSWORD="************";

@Component({
  selector: 'app-reviews-configuration',
  templateUrl: './reviews-configuration.component.html',
  styleUrls: ['./reviews-configuration.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS }
  ]
})
export class ReviewsConfigurationComponent implements OnInit, HasUnsavedChanges {
  event: Event;
  similarityVerificationUsername: string = '';
  similarityVerificationPassword: string = '';
  reviewConfig: EventReviewConfiguration;
  reviewConfigForm: FormGroup;
  /** Snapshot dos VALORES do form no último "sincronizado" (após initForm ou
   *  após save bem-sucedido). Serve de base pra `isDirty()` — comparação de
   *  valores é mais confiável que o flag `.dirty` do reactive form, que fica
   *  true em qualquer setValue/patchValue mesmo se o valor final é idêntico
   *  ao original (falso positivo comum). Comparação por JSON.stringify. */
  private reviewConfigSnapshot: string | null = null;

  loading = false;

  anonymityChoices = this.toSelectable(AnonymityValues);

  viewersReviewChoice = this.toSelectable(ViewersValues);
  viewersIdentityChoice = this.toSelectable(ViewersWithoutOwnValues);
  desableToggleAuthorsCanViewScores = false;

  @ViewChild(DatetimepickerInputComponent) dateTimePicker: DatetimepickerInputComponent;

  constructor(
    private adminService: AdminService,
    private fb: FormBuilder,
    public systemService: SystemService,
    public eventsService: EventsService,
    public notificationService: NotificationService,
    private reviewsService: ReviewsConfigurationService,
    private dialog: MatDialog,
    private timezoneService: TimezoneService
  ) { }

  ngOnInit() {
    this.reviewConfig = new EventReviewConfiguration();

    setTimeout(() => {
      this.event = this.adminService.selectedEvent;

      if (!this.event) {
        this.adminService.getEvent().subscribe(event => {
          this.event = event;
          this.reviewsService.getReviewsConfiguration(this.event.id).subscribe(data => {
            this.reviewConfig = data;
            this.initReviewConfigForm();
          });
        });

      } else {
        this.reviewsService.getReviewsConfiguration(this.event.id).subscribe(data => {
          this.reviewConfig = data;
          this.initReviewConfigForm();
        });
      }
    });
  }

  /** Dirty por VALORES (não pelo flag nativo do reactive form). Retorna
   *  true se o snapshot foi capturado E o estado atual difere dele.
   *  Usado pelo template (`[dirty]` do floating-submit-bar) e pelo guard
   *  (`hasUnsavedChanges`) — fonte única. */
  isDirty(): boolean {
    if (!this.reviewConfigForm || this.reviewConfigSnapshot == null) { return false; }
    return JSON.stringify(this.reviewConfigForm.getRawValue()) !== this.reviewConfigSnapshot;
  }

  private captureReviewConfigSnapshot(): void {
    this.reviewConfigSnapshot = this.reviewConfigForm
      ? JSON.stringify(this.reviewConfigForm.getRawValue())
      : null;
  }

  // Implementação de HasUnsavedChanges: o guard usa isso pra decidir se
  // intercepta a navegação com o dialog de confirmação.
  hasUnsavedChanges(): boolean {
    return this.isDirty();
  }

  toIterable(object): Array<string> {
    return Object.keys(object).filter(key => typeof object[key] !== 'function');
  }

  toSelectable(object): Array<{ id: number, value: string }> {
    return this.toIterable(object).map((key, index) => ({
      id: index,
      value: object[key].toString()
    }));
  }

  private getId(key, values) {
    const index = this.toIterable(values).findIndex(value => value === key);
    return index !== -1 ? index : null;
  }

  private validateDate(date) {
    return date ? moment(date.toString()) : null;
  }

  private initReviewConfigForm(): void {
    const usesSimVerification = this.reviewConfig.similarityVerification;
    this.reviewConfigForm = this.fb.group({
      doubleBlind: [this.reviewConfig.doubleBlind],
      similarityVerification: [this.reviewConfig.similarityVerification],
      docolocUsername: [{ value: usesSimVerification ? this.reviewConfig.docolocUsername : '', disabled: !usesSimVerification }],
      docolocPassword: [{ value: usesSimVerification ? MOCK_DOCOLOC_PASSWORD : '', disabled: !usesSimVerification }],

      anonymousReview: [this.reviewConfig.anonymousReview],
      authorsCanView: [this.reviewConfig.authorsCanView],
      authorsCanViewScores: [{ value: this.reviewConfig.authorsCanViewScores, disabled: !this.reviewConfig.authorsCanView }],
      tpcCanView: [this.getId(this.reviewConfig.tpcCanView, ViewersValues)],
      tpcViewReviewIdentity: [this.getId(this.reviewConfig.tpcViewReviewIdentity, ViewersWithoutOwnValues)],

      copyReviewAssigner: [this.reviewConfig.copyReviewAssigner],
      numberOfReviewsRequired: [this.reviewConfig.numberOfReviewsRequired, Validators.min(0)],

      // Sem campo na UI (input removido do template): os algoritmos de alocação
      // não usam este valor. Control MANTIDO só p/ o valor da base fazer
      // round-trip no PUT (getRawValue) e não ser zerado ao salvar. Reexpor na UI
      // se um algoritmo futuro passar a respeitá-lo.
      numberOfReviewsForTpcMember: [this.reviewConfig.numberOfReviewsForTpcMember, Validators.min(0)],
      journalDaysForReview: [this.reviewConfig.journalDaysForReview],
      conferenceDateDue: [this.validateDate(this.reviewConfig.conferenceDateDue)?.toDate(), Validators.required],
      timeZone: [this.timezoneService.browserTimeZone],
      reminderStartDate: [this.validateDate(this.reviewConfig.reminderStartDate)?.toDate()],
      daysBetweenReminders: [this.reviewConfig.daysBetweenReminders],

      blockReview: [this.reviewConfig.blockReview],
      tpcListAllPapers: [this.reviewConfig.tpcListAllPapers],
      showOnlyCompletedReviewToTpc: [this.reviewConfig.showOnlyCompletedReviewToTpc],
      paperRankingToTpc: [this.reviewConfig.paperRankingToTpc],

      spanLimitA: [this.reviewConfig.spanLimitA],
      spanLimitB: [this.reviewConfig.spanLimitB],

      tpcDelegate: [this.reviewConfig.tpcDelegate],
      tpcAssignToExternal: [this.reviewConfig.tpcAssignToExternal],
      tpcBidClaimPaper: [this.reviewConfig.tpcBidClaimPaper],
      claimPendingSubmissions: [this.reviewConfig.claimPendingSubmissions],

      discussionPeriodEnable: [this.reviewConfig.discussionPeriodEnable],
      discussionPeriodStart: [this.validateDate(this.reviewConfig.discussionPeriodStart)?.toDate()],
      timeZonePeriodStart: [this.timezoneService.browserTimeZone],
      discussionPeriodEnd: [this.validateDate(this.reviewConfig.discussionPeriodEnd)?.toDate()],
      timeZonePeriodEnd: [this.timezoneService.browserTimeZone],

      bidPeriodEnable: [this.reviewConfig.bidPeriodEnable],
      bidPeriodStart: [this.validateDate(this.reviewConfig.bidPeriodStart)?.toDate()],
      timeZoneBidStart: [this.timezoneService.browserTimeZone],
      bidPeriodEnd: [this.validateDate(this.reviewConfig.bidPeriodEnd)?.toDate()],
      timeZoneBidEnd: [this.timezoneService.browserTimeZone],

      rebuttalPeriodEnable: [this.reviewConfig.rebuttalPeriodEnable],
      rebuttalPeriodStart: [this.validateDate(this.reviewConfig.rebuttalPeriodStart)?.toDate()],
      timeZoneRebuttalStart: [this.timezoneService.browserTimeZone],
      rebuttalPeriodEnd: [this.validateDate(this.reviewConfig.rebuttalPeriodEnd)?.toDate()],
      timeZoneRebuttalEnd: [this.timezoneService.browserTimeZone]
    });
    this.desableToggleAuthorsCanViewScores = !this.reviewConfig.authorsCanView;
    this.toggleValidatorsSimilarityCredentials(this.reviewConfig.similarityVerification);
    this.toggleValidatorsDiscussionMessage(this.reviewConfig.discussionPeriodEnable);
    this.toggleValidatorsRebuttalMessage(this.reviewConfig.rebuttalPeriodEnable);
    this.toggleValidatorsBidPeriod(this.reviewConfig.bidPeriodEnable);
    this.captureReviewConfigSnapshot();
  }

  discardChanges() {
    // Form novo é pristine — o app-floating-submit-bar reage ao
    // [dirty]=false (via ngOnChanges) e pousa de volta ao lar sozinho.
    this.initReviewConfigForm();
  }

  submitReviewConfiguration() {
    if (!this.reviewConfigForm.valid) {
      this.reviewConfigForm.markAllAsTouched();
      this.notificationService.notifyError('admin.event.reviews.configuration.edit-reviews-config-invalid');
      return;
    }

    const reviewConfig = this.reviewConfigForm.getRawValue();

    reviewConfig.tpcCanView = this.toIterable(ViewersValues)[reviewConfig.tpcCanView];
    reviewConfig.tpcViewReviewIdentity = this.toIterable(ViewersWithoutOwnValues)[reviewConfig.tpcViewReviewIdentity];

    // Remove username e password se similarityVerification estiver desativado
    if (!reviewConfig.similarityVerification) {
      delete reviewConfig.docolocUsername;
      delete reviewConfig.docolocPassword;
    } else if (reviewConfig.docolocPassword) {
      // After its submitted, change the password to a mock
      this.reviewConfigForm.controls.docolocPassword.setValue(MOCK_DOCOLOC_PASSWORD);
    }

    this.loading = true;
    this.reviewsService.editReviewsConfiguration(this.event.id, reviewConfig).subscribe(
      reviewsConfig => {
        this.loading = false;
        this.reviewConfigForm.markAsPristine();
        // Novo snapshot pós-save — a partir daqui o estado atual é a nova
        // referência de "limpo"; o floating-submit-bar cai porque `isDirty()`
        // volta a false.
        this.captureReviewConfigSnapshot();
        // Sem notification de sucesso: o cycle do botão (Saving… → Salvar
        // alterações disabled) já comunica que persistiu. O floating-submit-bar
        // reage ao [dirty]=false e pousa de volta ao lar.
      },
      () => {
        this.loading = false;
      }
    );
  }

  disableAuthorsCanViewScores(ev){
    const ctrl = this.reviewConfigForm.get('authorsCanViewScores');
    if (ev.checked){
      this.desableToggleAuthorsCanViewScores = false;
      ctrl?.enable({ emitEvent: false });
    }else{
      this.desableToggleAuthorsCanViewScores = true;
      ctrl?.disable({ emitEvent: false });
    }
  }

  toggleValidatorsSimilarityCredentials(isEnabled: boolean): void {
    const usernameControl = this.reviewConfigForm.get('docolocUsername');
    const passwordControl = this.reviewConfigForm.get('docolocPassword');

    if (isEnabled) {
      usernameControl?.setValidators([]); // Permite valores opcionais
      passwordControl?.setValidators([]);
      usernameControl?.enable({ emitEvent: false });
      passwordControl?.enable({ emitEvent: false });
    } else {
      usernameControl?.clearValidators();
      passwordControl?.clearValidators();
      usernameControl?.disable({ emitEvent: false });
      passwordControl?.disable({ emitEvent: false });
    }

    usernameControl?.updateValueAndValidity();
    passwordControl?.updateValueAndValidity();
  }

  private toggleValidatorsDiscussionMessage(isEnabled: boolean) {
    const controlDiscussionPeriodStart = this.reviewConfigForm.get('discussionPeriodStart');
    const controlDiscussionPeriodEnd = this.reviewConfigForm.get('discussionPeriodEnd');

    if (isEnabled) {
      controlDiscussionPeriodStart?.setValidators([Validators.required]);
      controlDiscussionPeriodEnd?.setValidators([Validators.required]);
    } else {
      controlDiscussionPeriodStart?.clearValidators();
      controlDiscussionPeriodEnd?.clearValidators();
    }

    controlDiscussionPeriodStart?.updateValueAndValidity();
    controlDiscussionPeriodEnd?.updateValueAndValidity();
  }

  toggleValidatorsBidPeriod(value) {
    const controlBidStart = this.reviewConfigForm.get('bidPeriodStart');
    const controlBidEnd = this.reviewConfigForm.get('bidPeriodEnd');

    if (value) {
      controlBidStart.setValidators([Validators.required]);
      controlBidEnd.setValidators([Validators.required]);
    } else {
      controlBidStart.clearValidators();
      controlBidEnd.clearValidators();
    }

    controlBidStart.updateValueAndValidity();
    controlBidEnd.updateValueAndValidity();
  }

  toggleValidatorsRebuttalMessage(value) {
    const controlRebuttalPeriodStart = this.reviewConfigForm.get('rebuttalPeriodStart');
    const controlRebuttalPeriodEnd = this.reviewConfigForm.get('rebuttalPeriodEnd');

    if (value) {
      controlRebuttalPeriodStart.setValidators([Validators.required]);
      controlRebuttalPeriodEnd.setValidators([Validators.required]);
    } else {
      controlRebuttalPeriodStart.clearValidators();
      controlRebuttalPeriodEnd.clearValidators();
    }

    controlRebuttalPeriodStart.updateValueAndValidity();
    controlRebuttalPeriodEnd.updateValueAndValidity();
  }

  setGlobalDueDate() {
    const dueAt = this.reviewConfigForm.value.conferenceDateDue;
    const formattedDate = this.formatDateTime(this.dateTimePicker.dateTimeViewed);

    const timeZone = this.reviewConfigForm.value.timeZone;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'admin.event.reviews.configuration.review-global-due-at.title',
        content: 'admin.event.reviews.configuration.review-global-due-at.content',
        cancel: false,
        params: {
          content: {
            date: formattedDate,
            offset: this.timezoneService.getUTCTimeZone(timeZone)
          }
        }
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        forkJoin([
          this.reviewsService.editGlobalReviewDueDate(this.event.id, dueAt),
          this.reviewsService.editReviewsConfiguration(this.event.id, { conferenceDateDue: dueAt })
        ]).subscribe(
          () => {
            this.notificationService.notify('admin.event.reviews.configuration.review-global-due-at.notification-success', {
              params: {
                date: formattedDate // TODO: Pegar isso do retorno da API, para confirmar
              }
            });
          },
          () => {
            this.notificationService.notifyError('admin.event.reviews.configuration.review-global-due-at.notification-error');
          }
        );
      }
    });
  }
  resetPasswordInput():void {
    if(this.reviewConfigForm.get("docolocPassword").value == MOCK_DOCOLOC_PASSWORD){
      this.reviewConfigForm.controls.docolocPassword.setValue("");
    }
  }
  private formatDateTime(date: Date): string {
    if (getLocale() === 'pt-BR') {
      return toDayjs(date).format('HH:mm:ss DD/MM/YYYY')
    }

    return toDayjs(date).format('HH:mm:ss MM/DD/YYYY')
  }
}
